import { useState, type ReactElement, type ReactNode } from 'react';
import {
  IconAlertTriangle, IconArrowBackUp, IconBuildingBridge, IconBuildingWarehouse, IconChevronDown, IconChevronRight, IconHistory, IconCoin, IconChartBar, IconCurrencyDollar, IconDatabase, IconDownload, IconFlame, IconGasStation, IconInfoSquare,
  IconLock, IconMapPin, IconMinus, IconPlus, IconRadar, IconRefresh, IconRoute, IconRuler2, IconSailboat, IconSparkles, IconTicket, IconTrash, IconTruck, IconTruckLoading, IconArrowLeft,
  IconTarget, IconChartPie, IconSettings, IconArrowUp, IconArrowDown, IconEqual, IconBox,
} from '@tabler/icons-react';
import { Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import './i18n';
import { Button, Segmented, StateView, useToast, type ScreenState } from '@hana/ui';
import { useNavigate } from '@tanstack/react-router';
import {
  FUELNOW, HIST0, NOW_AT, PL, PTS0, ROUTE_CODE, SAVED0, SET, TIERS, autoShapeOf, calc, clampEr, d1, d2, defLoads, legKm, parseEr, parseKm, pc1, th,
  type Cargo, type Saved, type Shape,
} from './routePrice';
import s from './routePrice.module.css';

// Mint-TMS-RoutePrice (desktop 1536) · tính giá TRF theo chuỗi điểm. Chỉ đọc + thao tác giả lập trên dữ liệu mẫu (toast, Hoàn tác).
// Tiền chỉ hiện khi vai trò xem thử = Quản lý (quyền money thật nối sau). Chưa làm: khổ iPad (`Mint-TMS-RoutePrice-iPad`), khổ 1920, hộp thoại Lịch sử giá chi tiết.

type Role = 'mgr' | 'dsp' | 'none';
const MASK = '••••';
const SHK: Record<Shape, string> = { OW: 'Một chiều', RT_RETRACE: 'Khứ hồi', RT_LOOP: 'Vòng' };
const SHN: Record<Shape, string> = { OW: 'một chiều OW', RT_RETRACE: 'khứ hồi RT_RETRACE', RT_LOOP: 'vòng RT_LOOP' };
const MIXC = { sal: 'var(--d-blue)', fuel: 'var(--d-amber)', veh: 'var(--d-violet)', vetc: 'var(--err-bd)', oth: 'var(--d-gray)' } as const; // token-ok: board dùng hồng cho VETC, chưa có token → luong-a

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function Sec({ id, title, icon, shut, onFold, extra, children }: { id: string; title: string; icon: ReactElement; shut: boolean; onFold: (k: string) => void; extra?: ReactNode; children: ReactNode }) {
  return (
    <section className={s.sec} aria-label={title}>
      <h3 className={s.h3}>
        <span className={s.h3i} aria-hidden="true">{icon}</span>{title}
        <span className={s.grow} />
        {extra}
        <button type="button" className={s.fold} aria-expanded={!shut} aria-label={`${shut ? 'Mở' : 'Thu gọn'} ${title}`} title={`${shut ? 'Mở' : 'Thu gọn'} ${title}`} onClick={() => onFold(id)}>
          {shut ? <IconChevronRight size={16} aria-hidden="true" /> : <IconChevronDown size={16} aria-hidden="true" />}
        </button>
      </h3>
      {!shut && children}
    </section>
  );
}

export function RoutePricePage() {
  const T = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const forced = readState();
  const state: ScreenState = forced ?? 'ready';
  const [cargo, setCargo] = useState<Cargo>('RF');
  const [pts, setPts] = useState<readonly string[]>(PTS0);
  const [loadsOv, setLoadsOv] = useState<boolean[] | null>(null);
  const [shapeOv, setShapeOv] = useState<Shape | null>(null);
  const [kmRaw, setKmRaw] = useState<string | null>(null);
  const [erRaw, setErRaw] = useState<string | null>(null);
  const [qo, setQo] = useState<Record<string, number>>({});
  const [role, setRole] = useState<Role>(forced === 'noperm' ? 'none' : 'mgr');
  const [shut, setShut] = useState<Record<string, boolean>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [ask, setAsk] = useState(false);
  const [saved, setSaved] = useState<Saved>(SAVED0);
  const [hist, setHist] = useState(HIST0);
  const [histOpen, setHistOpen] = useState(false);

  const isMgr = role === 'mgr', isDsp = role === 'dsp', isNone = role === 'none';
  const showMoney = isMgr;
  const can = !isNone;
  const ro = (fn: () => void) => (can ? fn : () => toast({ msg: 'Chỉ xem.' }));
  const fold = (k: string) => setShut((x) => ({ ...x, [k]: !x[k] }));

  const S = SET[cargo];
  const autoShape = autoShapeOf(pts);
  const shape = shapeOv ?? autoShape;
  const loads = loadsOv && loadsOv.length === pts.length - 1 ? loadsOv : defLoads(pts.length - 1, autoShape);
  const legs = pts.slice(0, -1).map((c, i) => legKm(c, pts[i + 1] ?? c));
  const vmKm = Math.round(legs.reduce((a, b) => a + b, 0) * 10) / 10;
  const kmNum = kmRaw == null ? vmKm : parseKm(kmRaw);
  const kmBad = kmRaw != null && !(kmNum > 0);
  const km = kmBad ? vmKm : kmNum;
  const emptyLegKm = legs.reduce((a, b, i) => a + (loads[i] ? 0 : b), 0);
  const erAutoRaw = vmKm > 0 ? emptyLegKm / vmKm : 0.5;
  const erAuto = Math.round(clampEr(erAutoRaw) * 1000) / 1000;
  const erNum = erRaw == null ? erAuto : parseEr(erRaw);
  const erBad = erRaw != null && !(erNum >= 0.05 && erNum <= 0.9);
  const erFixed = shape === 'RT_RETRACE';
  const input = { cargo, shape, km, er: erBad ? erAuto : erNum, nPts: pts.length, fuelPx: FUELNOW.px, qo };
  const R = calc({ ...input, fares: null });
  const SNAP = calc({ ...input, fuelPx: saved.fuelPx, fares: saved.fares });
  const extra = R.extra;
  const nPass = R.vt.reduce((a, x) => a + x.qty, 0);
  const place = (c: string) => PL[c] ?? (['WH', c, '', 0, 0] as const);
  const routeK = pts.map((c) => place(c)[1]).join(' → ');
  const role3 = (i: number) => (i === 0 ? 'Lấy' : i === pts.length - 1 ? 'Trả' : i === 1 ? 'Giao' : 'Chen');
  const ROLE_CLS: Record<string, string | undefined> = { 'Lấy': s.rLay, 'Giao': s.rGiao, 'Chen': s.rChen, 'Trả': s.rTra };

  const undoable = (msg: string, undo: () => void) => toast({ msg, undo });
  const delPt = (i: number) => {
    const prev = { pts, loadsOv, kmRaw };
    setPts(pts.filter((_, j) => j !== i)); setLoadsOv(null); setKmRaw(null);
    undoable(`Đã bỏ ${place(pts[i]!)[1]}`, () => { setPts(prev.pts); setLoadsOv(prev.loadsOv); setKmRaw(prev.kmRaw); });
  };
  const addPt = (c: string) => {
    const prev = { pts, loadsOv, kmRaw };
    setPts([...pts.slice(0, -1), c, pts[pts.length - 1]!]); setLoadsOv(null); setKmRaw(null); setAddOpen(false);
    undoable(`Đã thêm ${place(c)[1]}`, () => { setPts(prev.pts); setLoadsOv(prev.loadsOv); setKmRaw(prev.kmRaw); });
  };
  const addList = Object.keys(PL).filter((c) => PL[c]![0] === 'WH' && !pts.includes(c));

  const dP = R.price - saved.price;
  const dPct = saved.price ? dP / saved.price : 0;
  const nowSaved = () => {
    if (!isMgr) { toast({ msg: 'Chỉ xem.' }); return; }
    if (kmBad || erBad) { toast({ msg: 'Sửa ô đang báo lỗi trước khi lưu', tone: 'err' }); return; }
    const prev = { saved, hist };
    setSaved({ price: R.price, at: NOW_AT, by: 'DAVID', fuelPx: FUELNOW.px, fares: {} });
    setHist([[R.price, NOW_AT, 'DAVID', FUELNOW.px], ...hist]); setAsk(false);
    undoable(`Đã lưu giá TRF ${ROUTE_CODE} · ${th(R.price)} ₫`, () => { setSaved(prev.saved); setHist(prev.hist); });
  };

  const mixRaw: [keyof typeof MIXC, string, number, string][] = [
    ['sal', 'Lương', R.sal, `${th(S.salB)} + ${th(R.tier[2])} × ${d1(R.runKm)} km → ${th(R.salBase)}${R.stopDrv ? ` + chen ${th(R.stopDrv)}` : ''}`],
    ['fuel', 'Dầu', R.fuelCost, `${d1(R.fuelL)} L × ${th(FUELNOW.px)}`],
    ['veh', 'Xe', R.vehCost, `${th(S.vcUnit)} × ${d1(R.runKm)} + ${th(S.vcBasic)}`],
    ['vetc', 'VETC', R.vetc, `${R.vt.length} trạm · ${nPass} lượt`],
    ['oth', 'Khác', S.oth, 'OTH theo loại hàng'],
  ];
  const top = [...mixRaw].sort((a, b) => b[2] - a[2])[0]!;
  const cmp = (k: string, a: number, b: number) => { const d = Math.round(a - b); return d ? `${k} ${d > 0 ? '+' : '−'}${th(Math.abs(d))}` : ''; };
  const snapDiff = [cmp('lương', R.sal, SNAP.sal), cmp('dầu', R.fuelCost, SNAP.fuelCost), cmp('xe', R.vehCost, SNAP.vehCost), cmp('VETC', R.vetc, SNAP.vetc), cmp('LN mục tiêu', R.tp, SNAP.tp)].filter(Boolean);
  const askLines: [string, string][] = [
    [`Giá ${th(R.price)} ₫ = chi phí ${th(R.tot)} + lợi nhuận mục tiêu ${th(R.tp)}, làm tròn 100.000 (chưa tròn ${th(R.raw)}).`, 'var(--pri)'],
    [`Tuyến ${SHN[shape]} ${d1(km)} km → xe chạy ${d1(R.runKm)} km, tra bậc theo ${d1(R.tierKm)} km: ${R.tier[0]}, lương ${th(R.salBase)} (tròn 100.000)${R.stopDrv ? ` + ${extra} điểm chen × ${th(S.stopDrv)}` : ''}.`, 'var(--d-blue)'],
    [R.rule === 'gt100' ? `Bậc > 100 km nên lợi nhuận mục tiêu = lương gốc × 1,7 = ${th(R.tpBase)}${R.stopCo ? ` + điểm chen ${th(R.stopCo)}` : ''}.` : `Bậc ${R.rule === 'le35' ? '≤ 35' : '≤ 100'} km nên lợi nhuận mục tiêu cố định ${th(R.tpBase)}${R.stopCo ? ` + điểm chen ${th(R.stopCo)}` : ''}.`, 'var(--ok)'],
    [`${top[1]} lớn nhất, ${Math.round((top[2] / R.tot) * 100)}% chi phí. Dầu ${d1(R.fuelL)} L, tỉ lệ rỗng ${d2(R.er)}.`, 'var(--d-amber)'],
    [`So với giá đang lưu ${th(saved.price)} (${saved.at.slice(0, 6)}): ${dP === 0 ? 'không đổi' : `${dP > 0 ? '+' : '−'}${th(Math.abs(dP))}`}${snapDiff.length ? ` · chi phí đổi do ${snapDiff.join(', ')}${saved.fuelPx !== FUELNOW.px ? ` (giá dầu ${th(saved.fuelPx)} → ${th(FUELNOW.px)})` : ''}` : ''}.`, dP > 0 ? 'var(--warn)' : 'var(--lb)'],
  ];

  const srcSet = `Thiết lập TRF · ${S.k}`, srcSetTip = `Thiết lập giá thành TRF theo loại hàng ${S.k}`;
  const srcTier = 'Bậc lương theo km', srcTierTip = `Dòng có mốc km ≤ ${d1(R.tierKm)} km lớn nhất`;
  const srcFuel = `Giá dầu ${FUELNOW.at.slice(0, 6)}`, srcFuelTip = `Giá dầu áp dụng mới nhất · ${FUELNOW.at}`;
  type Lk = [string, string, string, string, boolean, string?];
  const lkTable = (rows: Lk[]) => (
    <div className={s.lk}>
      {rows.map(([k, v, src, srcTip, money, tip]) => (
        <div key={k} className={s.lkRow}>
          <span title={tip ?? k}>{k}</span>
          <span className={money && !showMoney ? s.lkLock : s.lkV} title={money && !showMoney ? 'Không có quyền xem' : tip ?? k}>{money && !showMoney ? <><IconLock size={12} aria-hidden="true" />{MASK}</> : v}</span>
          <span className={s.lkSrc} title={srcTip}><span><IconDatabase size={12} aria-hidden="true" />{src}</span></span>
        </div>
      ))}
    </div>
  );
  const lkSal: Lk[] = [['Bậc', `${R.tier[0]} · từ ${R.tier[1]} km`, srcTier, srcTierTip, false], ['Lương theo km', `${th(R.tier[2])} đ/km`, srcTier, srcTierTip, true], ['Lương cơ bản', `${th(S.salB)} ₫`, srcSet, srcSetTip, true]];
  const lkFuel: Lk[] = [['Định mức tải', `${S.sfFull} L/100km`, srcSet, srcSetTip, false], ['Định mức rỗng', `${S.sfEmpty} L/100km`, srcSet, srcSetTip, false], ['Giá dầu', `${th(FUELNOW.px)} ₫/L`, srcFuel, srcFuelTip, false]];
  const lkVeh: Lk[] = [['Chi phí xe cơ bản', `${th(S.vcBasic)} ₫`, srcSet, srcSetTip, true], ['Chi phí xe theo km', `${th(S.vcUnit)} đ/km`, srcSet, srcSetTip, true], ['Khác (OTH)', `${th(S.oth)} ₫`, srcSet, srcSetTip, true], ['Phụ phí điểm chen', `${th(S.stopTot)} · TX ${th(S.stopDrv)}`, srcSet, srcSetTip, true, 'Phụ phí mỗi điểm chen · phần tài xế']];

  const subline = (
    <div className={s.subline}>
      <span className={s.routeK} title={pts.map((c, i) => `${role3(i)} ${place(c)[1]}`).join(' → ')}>{routeK}</span>
      <span className={s.chip} title={`Km tuyến${kmRaw != null && !kmBad ? ' · đã sửa' : ' · Vietmap'}`}>{`${d1(km)} km`}</span>
      <span className={`${s.chip} ${s.chipB}`} title={`${SHK[shape]} · ${shape}`}>{shape}</span>
      <span className={s.grow} />
      <span className={s.xem}>{T('tms.rp.viewAs')}</span>
      <Segmented kind="toggle" label="Xem thử theo vai trò" value={role} onChange={(k) => (forced === 'noperm' ? toast({ msg: 'Không có quyền xem giá TRF.' }) : setRole(k))} items={[{ key: 'mgr', label: 'Quản lý' }, { key: 'dsp', label: 'Điều độ' }, { key: 'none', label: 'Không quyền' }]} />
    </div>
  );

  const header = (
    <PageHeader findPad
      title="Tính giá TRF"
      findHint={false}
      lead={<button type="button" className={s.ico} aria-label="Về Tuyến" title="Về Tuyến" onClick={() => void navigate({ to: '/ezy/tms/routes' })}><IconArrowLeft size={18} aria-hidden="true" /></button>}
      meta={
        <span className={s.hChips}>
          <span className={s.code} title={`Mã tuyến ${ROUTE_CODE} · ${routeK}`}>{ROUTE_CODE}</span>
          <span className={`${s.cg} ${cargo === 'RF' ? s.cgRf : ''}`} title={cargo === 'RF' ? 'Hàng lạnh RF' : 'Hàng khô DC'}><i />{cargo === 'RF' ? 'RF' : 'Khô'}</span>
        </span>
      }
      actions={
        <>
          <button type="button" className={s.ico} aria-label="Tính lại" title="Tính lại" onClick={() => toast({ msg: `Đã tính lại · giá dầu ${FUELNOW.at.slice(0, 6)} · Vietmap ${d1(vmKm)} km` })}><IconRefresh size={18} aria-hidden="true" /></button>
          <button type="button" className={s.ico} aria-label="Lịch sử giá" title="Lịch sử giá" onClick={() => setHistOpen((x) => !x)}><IconHistory size={18} aria-hidden="true" /></button>
          <button type="button" className={s.ico} aria-label="Xuất" title="Xuất" onClick={() => (isNone ? toast({ msg: 'Chỉ xem.' }) : toast({ msg: isMgr ? `Đã xuất bảng tính giá ${ROUTE_CODE} · PDF` : `Đã xuất bảng vận hành ${ROUTE_CODE} · km, dầu, trạm` }))}><IconDownload size={18} aria-hidden="true" /></button>
          <Button variant="primary" onClick={nowSaved} title={isMgr ? `Lưu giá TRF ${th(R.price)} ₫` : 'Chỉ Quản lý lưu được giá'} disabled={!isMgr}>{T('tms.rp.save')}</Button>
        </>
      }
    />
  );

  const left = (
    <div className={s.colL} aria-label="Đầu vào">
      <div className={s.colH}><IconMapPin size={14} aria-hidden="true" /><span>{T('tms.rp.inputs')}</span></div>
      <Sec id="cg" title="Hàng & kiểu tuyến" icon={<IconBox size={16} />} shut={!!shut.cg} onFold={fold}>
        <div className={s.fld}><div className={s.lbl}>{T('tms.rp.cargo')}</div><Segmented kind="toggle" label="Loại hàng" value={cargo} onChange={(k) => ro(() => setCargo(k))()} items={[{ key: 'RF', label: 'RF' }, { key: 'DC', label: 'Khô' }]} /></div>
        <div className={s.fld}>
          <div className={s.lbl}>{T('tms.rp.shape')}</div>
          <div className={s.row}>
            <Segmented kind="toggle" label="Kiểu tuyến" value={shape} onChange={(k) => ro(() => setShapeOv(k))()} items={[{ key: 'OW', label: 'OW' }, { key: 'RT_RETRACE', label: 'RT_RETRACE' }, { key: 'RT_LOOP', label: 'RT_LOOP' }]} />
            <span className={`${s.tag} ${shapeOv && shapeOv !== autoShape ? s.tagW : s.tagOk}`} title={shapeOv && shapeOv !== autoShape ? `Kiểu tự nhận: ${autoShape}` : `Theo chuỗi điểm: ${autoShape === 'RT_LOOP' ? 'lấy và trả đều là cảng / depot' : 'điểm đầu hoặc cuối là kho'}`}><i />{shapeOv && shapeOv !== autoShape ? 'Đã sửa' : 'Tự nhận'}</span>
            {shapeOv && shapeOv !== autoShape && <button type="button" className={s.sq30} aria-label="Về kiểu tự nhận" title="Về kiểu tự nhận" onClick={() => setShapeOv(null)}><IconArrowBackUp size={15} aria-hidden="true" /></button>}
          </div>
        </div>
      </Sec>
      <Sec id="pts" title="Chuỗi điểm" icon={<IconRoute size={16} />} shut={!!shut.pts} onFold={fold}>
        <div>
          {pts.map((c, i) => {
            const p = place(c);
            const hasLeg = i < pts.length - 1;
            const on = hasLeg ? !!loads[i] : false;
            const r = role3(i);
            return (
              <div key={`${c}-${i}`}>
                <div className={s.pt}>
                  <span className={`${s.role} ${ROLE_CLS[r]}`} title={r === 'Chen' ? 'Điểm giao chen thêm · tính phụ phí điểm chen' : r === 'Lấy' ? 'Điểm lấy' : r === 'Trả' ? 'Điểm trả' : 'Điểm giao'}>{r}</span>
                  <span className={s.pic} aria-hidden="true">{p[0] === 'DP' ? <IconSailboat size={16} /> : <IconBuildingWarehouse size={16} />}</span>
                  <span className={s.pName} title={`${p[1]} · ${c}${p[2] ? ` · ${p[2]}` : ''}`}>{p[1]}</span>
                  <span className={s.pCode}>{c}</span>
                  {i > 0 && i < pts.length - 1 && pts.length > 2 && <button type="button" className={s.del} aria-label={`Bỏ điểm ${p[1]}`} title={`Bỏ điểm ${p[1]}`} onClick={ro(() => delPt(i))}><IconTrash size={15} aria-hidden="true" /></button>}
                </div>
                {hasLeg && (
                  <div className={`${s.leg} ${on ? s.legOn : ''}`}>
                    <span className={s.legKm} title={`Chặng ${p[1]} → ${place(pts[i + 1]!)[1]} · Vietmap`}><IconRuler2 size={13} aria-hidden="true" />{`${d1(legs[i]!)} km`}</span>
                    <button type="button" className={`${s.legB} ${on ? s.legBOn : ''}`} aria-pressed={on} aria-label={on ? 'Chặng chở hàng · đổi sang rỗng' : 'Chặng chạy rỗng · đổi sang tải'} title={on ? 'Chặng chở hàng · đổi sang rỗng' : 'Chặng chạy rỗng · đổi sang tải'}
                      onClick={ro(() => { const nl = loads.slice(); nl[i] = !on; setLoadsOv(nl); setErRaw(null); })}>
                      {on ? <IconTruckLoading size={13} aria-hidden="true" /> : <IconTruck size={13} aria-hidden="true" />}{on ? 'Tải' : 'Rỗng'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        <div className={s.addRow}>
          <button type="button" className={s.add} aria-haspopup="listbox" aria-expanded={addOpen} onClick={ro(() => setAddOpen(!addOpen))}><IconPlus size={15} aria-hidden="true" />{T('tms.rp.addStop')}</button>
          <span className={s.grow} />
          <span className={s.tag2} title={`Điểm chen = số điểm − 3 · ${pts.length} điểm`}><IconMapPin size={13} aria-hidden="true" />{`${extra} điểm chen`}</span>
          {addOpen && (
            <>
              <div className={s.scrim} aria-hidden="true" onClick={() => setAddOpen(false)} />
              <div className={s.pop} role="listbox" aria-label="Chọn điểm giao">
                {addList.map((c) => (
                  <button key={c} type="button" role="option" aria-selected="false" className={s.opt} title={`Thêm ${PL[c]![1]} trước điểm trả`} onClick={() => addPt(c)}>
                    <IconBuildingWarehouse size={15} aria-hidden="true" /><span>{PL[c]![1]}</span><em>{`${c} · ${PL[c]![2]}`}</em>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        </div>
      </Sec>
      <Sec id="km" title="Quãng đường" icon={<IconRuler2 size={16} />} shut={!!shut.km} onFold={fold}>
        <div className={s.two}>
          <div>
            <div className={s.lbl}>{T('tms.rp.km')}</div>
            <label className={`${s.inp} ${kmBad ? s.inpErr : ''}`}><input aria-label="Km tuyến" inputMode="decimal" value={kmRaw ?? d1(vmKm)} onChange={(e) => ro(() => setKmRaw(e.target.value))()} /><span>km</span></label>
            <div className={s.sub2}>
              <span className={`${s.tag3} ${kmRaw != null ? '' : s.tag3B}`} title={`Km định tuyến Vietmap theo chuỗi điểm · ${legs.map(d1).join(' + ')}`}><IconRadar size={13} aria-hidden="true" />{`Vietmap ${d1(vmKm)}`}</span>
              {kmRaw != null && <button type="button" className={s.sq26} aria-label="Về km Vietmap" title="Về km Vietmap" onClick={() => setKmRaw(null)}><IconArrowBackUp size={14} aria-hidden="true" /></button>}
              {kmBad && <span className={s.errTx}>{T('tms.rp.kmBad')}</span>}
            </div>
          </div>
          <div>
            <div className={s.lbl}>{T('tms.rp.empty')}</div>
            {erFixed ? (
              <div className={s.fixed} title="Khứ hồi RT_RETRACE luôn dùng 0,50"><IconLock size={14} aria-hidden="true" />0,50</div>
            ) : (
              <label className={`${s.inp} ${erBad ? s.inpErr : ''}`}><input aria-label="Tỉ lệ rỗng" inputMode="decimal" value={erRaw ?? d2(erAuto)} onChange={(e) => ro(() => setErRaw(e.target.value))()} /><span>0,05–0,9</span></label>
            )}
            <div className={s.sub2}>
              <span className={`${s.tag3} ${erRaw != null && !erFixed ? s.tag3W : ''}`} title={erFixed ? 'Khứ hồi RT_RETRACE' : `Km chặng rỗng ${d1(emptyLegKm)} / ${d1(vmKm)} km = ${d2(erAutoRaw)}`}>{erFixed ? 'Cố định 0,50' : erRaw != null ? 'Đã sửa' : 'Tự tính từ chặng'}</span>
              {erRaw != null && !erFixed && <button type="button" className={s.sq26} aria-label="Về tỉ lệ tự tính" title="Về tỉ lệ tự tính" onClick={() => setErRaw(null)}><IconArrowBackUp size={14} aria-hidden="true" /></button>}
              {erBad && !erFixed && <span className={s.errTx}>Trong 0,05–0,9</span>}
            </div>
          </div>
        </div>
        <div className={s.tiles4}>
          {([[T('tms.rp.runKm'), d1(R.runKm), `Km xe chạy thực${shape === 'RT_RETRACE' ? ' = km × 2' : ' = km'}`], [T('tms.rp.tierKm'), d1(R.tierKm), `Km dùng tra bậc lương${shape === 'RT_LOOP' ? ' = km ÷ 2' : ' = km'}`], ['Km tải', d1(R.loadedKm), 'Km chở hàng = km chạy × (1 − tỉ lệ rỗng)'], ['Km rỗng', d1(R.emptyKm), 'Km chạy rỗng = km chạy × tỉ lệ rỗng']] as const).map(([k, v, tip]) => (
            <div key={k} className={s.tile} title={tip}><div>{k}</div><b>{v}</b></div>
          ))}
        </div>
        <div className={s.split} role="img" aria-label={`Tải ${Math.round((1 - R.er) * 100)}% · rỗng ${Math.round(R.er * 100)}%`} title={`Tải ${Math.round((1 - R.er) * 100)}% · rỗng ${Math.round(R.er * 100)}%`}><span style={{ width: `${Math.round((1 - R.er) * 100)}%` }} /><span className={s.splitE} /></div>
      </Sec>
      <Sec id="vt" title="Trạm VETC" icon={<IconBuildingBridge size={16} />} shut={!!shut.vt} onFold={fold}
        extra={<button type="button" className={s.sq30c} aria-label="Dò lại trạm trên tuyến" title="Dò lại trạm trên tuyến" onClick={ro(() => { setQo({}); toast({ msg: `Đã dò lại trạm trên tuyến · ${R.vt.length} trạm` }); })}><IconRadar size={16} aria-hidden="true" /></button>}>
        <div className={`${s.vRow} ${s.vHead} ${showMoney ? s.vM : s.vN}`}><span>{T('tms.rp.thStation')}</span><span>{T('tms.rp.thSys')}</span><span>{T('tms.rp.thPass')}</span><span className={s.r}>{T('tms.rp.ticketL5')}</span>{showMoney && <span className={s.r}>{T('tms.rp.amount')}</span>}</div>
        {R.vt.map((x) => {
          const kin = x.he === 'KIN';
          const ov = !kin && qo[x.id] != null && qo[x.id] !== x.auto;
          return (
            <div key={x.id} className={`${s.vRow} ${s.vBody} ${showMoney ? s.vM : s.vN}`}>
              <div className={s.vName}><span title={x.full}>{x.name}</span>{x.month && <span className={s.month} role="img" aria-label="Đội có vé tháng · giá tính theo vé lượt" title="Đội có vé tháng · giá tính theo vé lượt"><IconTicket size={13} aria-hidden="true" /></span>}</div>
              <span className={s.he} title={kin ? 'Hệ thu kín · khứ hồi tính 2 chiều' : 'Hệ thu mở · tính theo số lượt đi qua'}><i style={{ background: kin ? 'var(--d-violet)' : 'var(--d-amber)' }} />{kin ? 'Kín' : 'Mở'}</span>
              {kin ? (
                <span className={s.qK} title={x.qty === 2 ? '2 chiều' : '1 chiều'}>{x.qty === 2 ? '2 chiều' : '1 chiều'}</span>
              ) : (
                <div className={s.qty} role="group" aria-label={`Số lượt qua ${x.name}`}>
                  <button type="button" aria-label={`Bớt 1 lượt ${x.name}`} title={`Bớt 1 lượt ${x.name}`} onClick={ro(() => (x.qty <= 1 ? toast({ msg: 'Tối thiểu 1 lượt' }) : setQo({ ...qo, [x.id]: x.qty - 1 })))}><IconMinus size={13} aria-hidden="true" /></button>
                  <span className={ov ? s.warnTx : ''} title={ov ? `Đã sửa · tự đếm ${x.auto} lượt` : `${x.qty} lượt đi qua`}>{x.qty}</span>
                  <button type="button" aria-label={`Thêm 1 lượt ${x.name}`} title={`Thêm 1 lượt ${x.name}`} onClick={ro(() => setQo({ ...qo, [x.id]: x.qty + 1 }))}><IconPlus size={13} aria-hidden="true" /></button>
                </div>
              )}
              <span className={s.fare} title={kin ? `Vé lượt L5 · đi ${th(x.f)} · về ${th(x.back ?? 0)}` : `Vé lượt L5 · ${th(x.f)} ₫/lượt`}>{th(x.f) + (kin && x.qty === 2 ? ' ×2' : '')}</span>
              {showMoney && <b className={s.amt}>{th(x.amt)}</b>}
            </div>
          );
        })}
        <div className={s.vFoot}>
          <span className={s.src} title="Giá vé lượt loại 5 · mọi trạm tính theo vé lượt"><IconDatabase size={12} aria-hidden="true" />{T('tms.rp.vetcTariff')}</span>
          <span className={s.cnt} title="Kín khứ hồi tính 2 chiều · mở theo lượt thực">{`${R.vt.length} trạm · ${nPass} lượt`}</span>
          <span className={s.grow} />
          {showMoney && <><span>{T('tms.rp.vetcTot')}</span><b>{`${th(R.vetc)} ₫`}</b></>}
        </div>
      </Sec>
    </div>
  );

  const mid = (
    <div className={s.colM} aria-label="Bảng tra đang áp">
      <div className={s.colH}><IconSettings size={14} aria-hidden="true" /><span>{T('tms.rp.table')}</span><span className={s.grow} /><span className={s.at} title={`Bảng tra đọc lúc ${NOW_AT}`}>{`Áp lúc ${NOW_AT.slice(0, 6)} ${NOW_AT.slice(-5)}`}</span></div>
      <Sec id="lsal" title="Lương tài xế" icon={<IconChartBar size={16} />} shut={!!shut.lsal} onFold={fold}>
        <div className={s.stk10}>
        <div className={s.ladder} role="img" aria-label={`Bậc lương theo km, đang áp ${R.tier[0]}`}>
          {TIERS.map((x, i) => {
            const nx = TIERS[i + 1];
            const r = `${nx ? `${x[1]}–${nx[1] - 1}` : `≥ ${x[1]}`} km`;
            return <div key={x[0]} className={x[0] === R.tier[0] ? s.lOn : ''} title={`${x[0]} · ${nx ? `${x[1]}–${nx[1] - 1} km` : `từ ${x[1]} km`}${showMoney ? ` · ${th(x[2])} đ/km` : ''}`}><div><span>{x[0]}</span><span>{r}</span></div></div>;
          })}
        </div>
        {lkTable(lkSal)}
        </div>
      </Sec>
      <Sec id="lfuel" title="Dầu" icon={<IconGasStation size={16} />} shut={!!shut.lfuel} onFold={fold}>{lkTable(lkFuel)}</Sec>
      <Sec id="lveh" title="Xe & khác" icon={<IconTruck size={16} />} shut={!!shut.lveh} onFold={fold}>{lkTable(lkVeh)}</Sec>
    </div>
  );

  const colHR = <div className={s.colH}><IconCoin size={14} aria-hidden="true" /><span>{T('tms.rp.result')}</span></div>;
  const right = isMgr ? (
    <div className={s.colR} aria-label="Kết quả">
      {colHR}
      <Sec id="res" title="Giá TRF" icon={<IconCurrencyDollar size={16} />} shut={!!shut.res} onFold={fold}>
        <div className={s.price}>
          <div title="Giá TRF đã làm tròn 100.000">{`${th(R.price)} ₫`}</div>
          <span title="Chi phí + lợi nhuận mục tiêu trước làm tròn">{`chưa tròn ${th(R.raw)}`}</span>
        </div>
        <div className={s.tiles3}>
          {([['Tổng chi phí', th(R.tot), 'Lương + Dầu + Xe + VETC + Khác', ''], ['Lợi nhuận MT', th(R.tp), 'Lợi nhuận mục tiêu theo bậc km', ''], ['Margin', pc1(R.margin), '(Giá − Chi phí) / Giá', R.margin < 0 ? s.tErr : s.tOk]] as const).map(([k, v, tip, c]) => (
            <div key={k} className={`${s.tile} ${c}`} title={tip}><div>{k}</div><b>{v}</b></div>
          ))}
        </div>
        <div className={s.savedRow}>
          <span className={s.av} title={`${saved.by}`} aria-hidden="true">{saved.by.slice(0, 2)}</span>
          <span title={`Giá đang lưu ${th(saved.price)} ₫ · ${saved.at} · ${saved.by}`}>{`${th(saved.price)} · ${saved.at.slice(0, 6)}`}</span>
          <span className={`${s.delta} ${dP > 0 ? s.dUp : dP < 0 ? s.dDn : ''}`} title="Chênh so với giá đang lưu">
            {dP > 0 ? <IconArrowUp size={13} aria-hidden="true" /> : dP < 0 ? <IconArrowDown size={13} aria-hidden="true" /> : <IconEqual size={13} aria-hidden="true" />}
            {dP === 0 ? '0' : `${dP > 0 ? '+' : '−'}${th(Math.abs(dP))} · ${dPct > 0 ? '+' : '−'}${pc1(Math.abs(dPct))}`}
          </span>
        </div>
        <button type="button" className={s.askB} aria-expanded={ask} onClick={() => setAsk(!ask)}><IconSparkles size={16} aria-hidden="true" />{T('tms.rp.explain')}</button>
        {ask && (
          <div className={s.askBox} role="region" aria-label="Ask HANA · Giải thích giá">
            {askLines.map(([k, dot]) => <div key={k}><span aria-hidden="true" style={{ background: dot }} /><span>{k}</span></div>)}
          </div>
        )}
      </Sec>
      <Sec id="mix" title="Cơ cấu chi phí" icon={<IconChartPie size={16} />} shut={!!shut.mix} onFold={fold}>
        <div className={s.bar} role="img" aria-label={`Cơ cấu chi phí: ${mixRaw.map(([, k, v]) => `${k} ${Math.round((v / R.tot) * 100)}%`).join(', ')}`}>
          {mixRaw.map(([id, k, v]) => <span key={id} title={`${k} · ${th(v)} ₫`} style={{ width: `${R.tot > 0 ? Math.max(1, Math.round((v / R.tot) * 1000) / 10) : 0}%`, background: MIXC[id] }} />)}
        </div>
        <div>
          {mixRaw.map(([id, k, v, d]) => (
            <div key={id} className={s.mixRow}>
              <span aria-hidden="true" style={{ background: MIXC[id] }} /><span>{k}</span><span className={s.mixD} title={`${k} · ${d}`}>{d}</span><b>{th(v)}</b><em>{`${R.tot > 0 ? Math.round((v / R.tot) * 100) : 0}%`}</em>
            </div>
          ))}
        </div>
        <div className={s.totRow}><b>{T('tms.rp.totalCost')}</b><b title="Lương + Dầu + Xe + VETC + Khác">{`${th(R.tot)} ₫`}</b></div>
      </Sec>
      <Sec id="tp" title="Lợi nhuận mục tiêu" icon={<IconTarget size={16} />} shut={!!shut.tp} onFold={fold}>
        <div className={s.tpTop}><b title="Lợi nhuận mục tiêu">{`${th(R.tp)} ₫`}</b><span>{`${R.tier[0]} · km tra bậc ${d1(R.tierKm)}`}</span></div>
        <div role="list" aria-label="Quy tắc lợi nhuận mục tiêu">
          {([['le35', 'Bậc ≤ 35 km', 'Cố định', 1500000], ['le100', 'Bậc ≤ 100 km', 'Cố định', 2000000], ['gt100', 'Bậc > 100 km', `${th(R.salBase)} × 1,7`, R.salBase * 1.7]] as const).map(([id, k, v, amt]) => {
            const on = R.rule === id;
            return <div key={id} role="listitem" className={`${s.rule} ${on ? s.ruleOn : ''}`} title={`${k} · ${v}${on ? ` · đang áp (km tra bậc ${d1(R.tierKm)})` : ''}`}><span aria-hidden="true" /><span>{k}</span><span>{v}</span><b>{th(amt)}</b></div>;
          })}
          <div role="listitem" className={`${s.rule} ${extra ? s.ruleX : ''}`} title="Phụ phí điểm chen phần công ty"><span aria-hidden="true" /><span>{T('tms.rp.extraStop')}</span><span>{`${extra} × (${th(S.stopTot)} − ${th(S.stopDrv)})`}</span><b>{th(R.stopCo)}</b></div>
        </div>
      </Sec>
    </div>
  ) : isDsp ? (
    <div className={s.colR} aria-label="Kết quả">
      {colHR}
      <Sec id="ops" title="Kết quả vận hành" icon={<IconFlame size={16} />} shut={!!shut.ops} onFold={fold}>
        <div className={s.ops}>
          {([[T('tms.rp.runKm'), `${d1(R.runKm)} km`, <IconRuler2 key="a" size={16} />], [T('tms.rp.tierKm'), `${d1(R.tierKm)} km · ${R.tier[0]}`, <IconInfoSquare key="b" size={16} />], [T('tms.rp.fuel'), `${d1(R.fuelL)} L`, <IconGasStation key="c" size={16} />], [T('tms.rp.loadEmpty'), `${d1(R.loadedKm)} / ${d1(R.emptyKm)}`, <IconTruckLoading key="d" size={16} />], [T('tms.rp.vetcStations'), `${R.vt.length} trạm · ${nPass} lượt`, <IconBuildingBridge key="e" size={16} />], [T('tms.rp.extraStop'), String(extra), <IconMapPin key="f" size={16} />]] as const).map(([k, v, ic]) => (
            <div key={k} className={s.opsT}><span aria-hidden="true">{ic}</span><div><div>{k}</div><b>{v}</b></div></div>
          ))}
        </div>
        <div className={s.lockK}>{T('tms.rp.noMoney')}</div>
      </Sec>
    </div>
  ) : (
    <div className={s.colR} aria-label="Kết quả">
      {colHR}
      <section className={s.noMoney} aria-label="Kết quả"><span aria-hidden="true"><IconLock size={22} /></span><div>•••••• ₫</div><div>{T('tms.rp.noTrf')}</div></section>
    </div>
  );

  return (
    <Page kind="table">
      {header}
      <div className={s.page}>
        <StateView
          state={state === 'noperm' ? 'ready' : state}
          icon={<IconRoute size={26} stroke={1.8} />}
          title={state === 'empty' ? 'Chưa chọn tuyến' : state === 'error' ? 'Không tải được dữ liệu. Đang hiện bản lưu gần nhất.' : undefined}
          action={state === 'empty' ? <Button variant="secondary" onClick={() => void navigate({ to: '/ezy/tms/routes' })}>{T('tms.rp.pickRoute')}</Button> : state === 'error' ? <Button variant="secondary" icon={<IconAlertTriangle size={16} />} onClick={() => toast({ msg: 'Đang tải lại…' })}>{T('tms.ov.retry')}</Button> : undefined}
          skeleton={<div className={s.grid} aria-busy="true">{[0, 1, 2].map((i) => <div key={i} className={s.skCol}>{[0, 1, 2].map((j) => <div key={j} className={s.sk} />)}</div>)}</div>}
        >
          {subline}
          <div className={s.grid}>{left}{mid}{right}</div>
          {histOpen && (
            <div className={s.hist} role="dialog" aria-label={`Lịch sử giá · ${ROUTE_CODE}`}>
              <div className={s.histH}><b>{`Lịch sử giá · ${ROUTE_CODE}`}</b><span>{`${hist.length} lần lưu`}</span><span className={s.grow} /><Button variant="secondary" onClick={() => setHistOpen(false)}>{T('tms.rp.close')}</Button></div>
              {hist.map(([p, at, by, fp], i) => <div key={at} className={s.histR}><i style={{ background: i === 0 ? 'var(--pri)' : 'var(--bd)' }} /><b>{`${showMoney ? th(p) : MASK} ₫ · ${by}`}</b><span>{`${at} · dầu ${th(fp)} ₫/L`}</span></div>)}
            </div>
          )}
        </StateView>
      </div>
    </Page>
  );
}
